import styled, { css } from 'styled-components'

type InputRadioButtonProps = {
  name: string
  value: string
  checked: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  label: string
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

type ToggleRadioButtonProps = {
  name: string
  value: string
  checked: boolean
  onChange: (value: string | null) => void
  label: string
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
}

const sizeMap = {
  small: 18,
  medium: 28,
  large: 38,
}

export const InputRadioButton = ({
  name,
  value,
  checked,
  onChange,
  label,
  disabled = false,
  size = 'medium',
}: InputRadioButtonProps) => (
  <Label disabled={disabled}>
    <HiddenRadio type='radio' name={name} value={value} checked={checked} onChange={onChange} disabled={disabled} />
    <CheckRadioButton checked={checked} disabled={disabled} size={size} />
    <span>{label}</span>
  </Label>
)

export const ToggleRadioButton = ({
  name,
  value,
  checked,
  onChange,
  label,
  disabled = false,
  size = 'medium',
}: ToggleRadioButtonProps) => {
  // 클릭 시: 이미 선택된 값이면 null, 아니면 value 전달
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return
    if (checked) {
      onChange(null)
    } else {
      onChange(value)
    }
  }

  return (
    <Label disabled={disabled}>
      <HiddenRadio
        type='checkbox'
        name={name}
        value={value}
        checked={checked}
        readOnly
        disabled={disabled}
        onClick={handleClick}
        tabIndex={0}
      />
      <CheckRadioButton checked={checked} disabled={disabled} size={size} />
      <span>{label}</span>
    </Label>
  )
}

const Label = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  gap: 8px;
  font-size: 16px;
  color: ${({ disabled }) => (disabled ? '#bbb' : 'inherit')};
`

const HiddenRadio = styled.input`
  display: none;
`

const CheckRadioButton = styled.span<{ checked: boolean; disabled?: boolean; size?: 'small' | 'medium' | 'large' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ size }) => (size ? sizeMap[size] : sizeMap.medium)}px;
  min-width: ${({ size }) => (size ? sizeMap[size] : sizeMap.medium)}px;
  height: ${({ size }) => (size ? sizeMap[size] : sizeMap.medium)}px;
  position: relative;
  border-radius: 50%;
  border-width: 1px;
  border-style: solid;
  background-color: var(--colWhite);
  border-color: var(--colBlack);
  transition: background 0.2s, border-color 0.2s;

  ${({ checked }) =>
    checked &&
    css`
      background-color: var(--colBlack);
      border-color: var(--colBlack);
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}

  &:after {
    content: '';
    width: ${({ size }) => (size === 'small' ? 5 : size === 'large' ? 12 : 8)}px;
    height: ${({ size }) => (size === 'small' ? 3 : size === 'large' ? 6 : 4)}px;
    margin-top: -1px;
    border-left: 2px solid var(--colWhite);
    border-bottom: 2px solid var(--colWhite);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    display: ${({ checked }) => (checked ? 'block' : 'none')};
  }
`
